import { CrudList } from '../shared/CrudPage';
import { licenseService, enumLabel } from '@kss/api';
import type { License, LicenseCreate, LicenseFilter } from '@kss/api';
import { StatusBadge } from '@kss/ui';
import { NameCell } from '../../hooks/useNameMaps';
import { isValidIsoDate } from '../../utils/dateValidation';

export default function AllLicenses() {
  return (
    <CrudList<License, LicenseCreate, LicenseFilter>
      title="All licenses" newLabel="New license" service={licenseService}
      idKey="licenseId" labelKey="licenseKey" basePath="/panel/licenses"
      columns={[
        { accessor: 'licenseKey', title: 'Key', sortable: true, filterable: true, render: (r) => <span className="font-mono text-xs">{r.licenseKey ?? '—'}</span> },
        // Plan id stays on the row for save round-tripping; visible cell
        // uses the subscription plan's name. NameCell shares one
        // subscriptionPlanService.all() call across every license row.
        {
          accessor: 'subscriptionPlanId',
          title: 'Plan',
          sortable: true,
          render: (r) => <NameCell kind="subscriptionPlan" id={r.subscriptionPlanId ?? r.planId} />,
        },
        {
          accessor: 'startsAt',
          title: 'Start',
          sortable: true,
          render: (r) => formatDate(r.startsAt ?? r.startDate),
        },
        {
          accessor: 'expiresAt',
          title: 'Expires',
          sortable: true,
          render: (r) => formatDate(r.expiresAt ?? r.endDate),
        },
        { accessor: 'maxUsers', title: 'Max users', sortable: true, textAlign: 'right', render: (r) => r.maxUsers ?? '—' },
        {
          accessor: 'status',
          title: 'Status',
          sortable: true,
          render: (r) => <StatusBadge status={enumLabel('LicenseStatus', typeof r.status === 'number' ? r.status : Number(r.status))} />,
        },
      ]}
    />
  );
}

/**
 * Defensive date formatter: rejects nonsense like "20-06-0020"
 * (which Date.parse would happily interpret as the year 20 AD) and
 * shows an em-dash instead, so admins immediately see corrupt rows.
 */
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  if (!isValidIsoDate(value)) return '—';
  return new Date(value).toLocaleDateString();
}
